import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { Bell, BellOff, Check } from 'lucide-react';
import {
    useNotifications,
    useMarkNotificationRead,
    type Notification,
} from '@/api/notifications';

function formatRelative(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = Date.now() - then;
    if (diffMs < 0) return 'just now';
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function NotificationDrawer() {
    const { data, isLoading } = useNotifications(true);
    const markRead = useMarkNotificationRead();
    const navigate = useNavigate();

    const unreadCount = useMemo(
        () => (data ?? []).filter((n) => n.unread).length,
        [data],
    );

    function handleRowClick(n: Notification) {
        if (n.unread) markRead.mutate(n.id);
        if (n.link) navigate(n.link);
    }

    async function markAllRead() {
        const unread = (data ?? []).filter((n) => n.unread);
        await Promise.all(unread.map((n) => markRead.mutateAsync(n.id)));
    }

    return (
        <Popover.Root>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    title="Notifications"
                    aria-label={
                        unreadCount > 0
                            ? `Notifications (${unreadCount} unread)`
                            : 'Notifications'
                    }
                    className="relative w-9 h-9 rounded-md flex items-center justify-center text-text-muted hover:bg-surface-muted transition-colors"
                >
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                        <span
                            className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-accent2-text text-white text-[10px] font-bold flex items-center justify-center tabular-nums"
                            aria-hidden="true"
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            </Popover.Trigger>

            <Popover.Portal>
                <Popover.Content
                    align="end"
                    sideOffset={6}
                    className="w-[380px] max-h-[520px] bg-surface border border-border rounded-md shadow-lg z-50 flex flex-col"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
                        <div className="text-sm font-semibold text-text">
                            Notifications
                            {unreadCount > 0 && (
                                <span className="ml-2 text-xs text-text-subtle font-normal">
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="text-xs text-accent-text hover:underline"
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {isLoading ? (
                            <div className="p-6 text-center text-xs text-text-subtle">Loading…</div>
                        ) : !data || data.length === 0 ? (
                            <div className="p-8 text-center text-text-subtle">
                                <BellOff className="w-5 h-5 mx-auto mb-2 opacity-40" />
                                <p className="text-xs">No notifications</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-border">
                                {data.map((n) => (
                                    <li
                                        key={n.id}
                                        className={
                                            'group flex items-start gap-2 px-3 py-2.5 hover:bg-surface-muted transition-colors ' +
                                            (n.unread ? 'bg-info-soft/40' : '')
                                        }
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleRowClick(n)}
                                            className="flex-1 min-w-0 text-left"
                                        >
                                            <div className="flex items-start gap-2">
                                                {n.icon && (
                                                    <span className="text-sm leading-none pt-0.5 shrink-0" aria-hidden="true">
                                                        {n.icon}
                                                    </span>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-baseline gap-2">
                                                        <span className={
                                                            'text-sm truncate ' +
                                                            (n.unread ? 'font-semibold text-text' : 'text-text')
                                                        }>
                                                            {n.title}
                                                        </span>
                                                        <span className="text-[11px] text-text-subtle shrink-0 whitespace-nowrap">
                                                            {formatRelative(n.time)}
                                                        </span>
                                                    </div>
                                                    {n.desc && (
                                                        <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                                                            {n.desc}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                        {n.unread && (
                                            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => markRead.mutate(n.id)}
                                                    title="Mark as read"
                                                    aria-label={`Mark "${n.title}" as read`}
                                                    className="w-6 h-6 rounded flex items-center justify-center text-text-subtle hover:text-text hover:bg-surface"
                                                >
                                                    <Check className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
